import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, TextField, Link, Stack, Card, CardContent } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DynamicsLogo from '../../components/images/DynamicsLogo';
import { verifyEmailAuth, verifyWhatsAppAuth, sendEmailAuth, sendWhatsAppAuth, verifyWhatsAppRegistration, verifyEmailRegistration } from '../../utils/api';
import { useAuth } from '../../context/AuthContext'; // Import useAuth from the context
import CircularProgress from '@mui/material/CircularProgress';

export default function OTPVerification() {
    const location = useLocation();
    const navigate = useNavigate();
    const otpInputRef = useRef(null);

    const { method, contact, password, name } = location.state || {}; // Added password for email verification
    const { login } = useAuth(); // Access login function from AuthContext

    const [otp, setOtp] = useState('');
    const [countdown, setCountdown] = useState(180);
    const [isResendAvailable, setIsResendAvailable] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    // Log the passed state for debugging
    useEffect(() => {
        console.log('Method:', method);
        console.log('Contact:', contact);
        console.log('Password:', password);
        console.log('Name:', name);
    }, [method, contact, password, name]);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
        } else {
            clearInterval(timer);
            setIsResendAvailable(true);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const handleResendOTP = async () => {
        console.log('Resending OTP...');
        setCountdown(180);
        setIsResendAvailable(false);
        setOtp('');
        otpInputRef.current?.focus();

        try {
            // Check if the method is 'email' or 'whatsapp' and call the respective resend function
            if (method === 'email') {
                console.log('Resending OTP to email:', contact);
                await sendEmailAuth(contact, password);  // Resend OTP for email
            } else if (method === 'whatsapp') {
                console.log('Resending OTP to WhatsApp:', contact);
                await sendWhatsAppAuth(contact);  // Resend OTP for WhatsApp
            }

            // After a successful resend, enable the resend button
            setIsResendAvailable(true);
        } catch (error) {
            console.error('Error resending OTP:', error);
            setErrorMessage('Failed to resend OTP. Please try again.');
        }
    };

    const handleChange = (event) => {
        setOtp(event.target.value);
    };

    const handleSubmitOTP = async () => {
        setLoading(true); // Start loading before the request

        try {
            console.log('Email/WhatsApp:', contact);  // Log the contact (email or whatsapp)
            console.log('Password:', password);  // Log the password for email verification
            console.log('Entered OTP:', otp);  // Log the OTP entered by the user

            let response;

            // Determine whether the user is registering or logging in, and whether it's via email or WhatsApp
            if (method === 'register-email') {
                // Email Registration
                response = await verifyEmailRegistration(name, contact, password, otp);
            } else if (method === 'register-whatsapp') {
                // WhatsApp Registration
                response = await verifyWhatsAppRegistration(contact, otp);
            } else if (method === 'login-email') {
                // Email Login
                response = await verifyEmailAuth(contact, password, otp);
            } else if (method === 'login-whatsapp') {
                // WhatsApp Login
                response = await verifyWhatsAppAuth(contact, otp);
            }

            console.log('OTP verification response:', response);

            // If the response is success, store the token in localStorage and update auth state
            if (response.status === 'success') {
                // Use login from AuthContext to set authentication state
                login(response.token);

                console.log('Login successful, token stored:', response.token);

                // Navigate to the next page after successful verification
                navigate('/home');
            } else {
                // Show error message from the API response
                setErrorMessage(response.message);
            }
        } catch (error) {
            // If there's an error with the API call or other issues, log and display it
            console.error('OTP verification error:', error);

            // Check if the error has a response and if it's an axios error
            if (error.response) {
                const errorMessage = error.response.data?.message || 'An unexpected error occurred. Please try again.';
                setErrorMessage(errorMessage);  // Display the error message from the API response
            } else {
                setErrorMessage('OTP verification failed. Please try again.');
            }
        } finally {
            setLoading(false); // Ensure loading state is always reset, even on success or failure
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f6f8',
                padding: 2,
            }}
        >
            <Card sx={{ maxWidth: 400, width: '100%', padding: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <DynamicsLogo src="/images/otp.webp" />
                    <Typography variant="h5" sx={{ textAlign: 'center', marginBottom: 2 }}>
                        Konfirmasi OTP
                    </Typography>
                    <Typography variant="body1" sx={{ textAlign: 'center', marginBottom: 2 }}>
                        Untuk melanjutkan, silakan masukkan One Time Passcode Anda yang telah kami kirimkan ke{' '}
                        <strong>
                            {method === 'register-email' || method === 'login-email' ? `Email ${contact}` : `WhatsApp ${contact}`}
                        </strong>
                    </Typography>
                    <TextField
                        label="OTP"
                        value={otp}
                        onChange={handleChange}
                        fullWidth
                        autoFocus
                        variant="outlined"
                        size="small"
                        slotProps={{
                            input: {
                                maxLength: 4,
                                inputMode: 'numeric',
                            },
                        }}
                        sx={{ marginBottom: 2 }}
                        inputRef={otpInputRef}
                    />
                    {errorMessage && (
                        <Typography variant="body2" color="error" sx={{ textAlign: 'center', marginBottom: 2 }}>
                            {errorMessage}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                        Tidak menerima kode OTP?{' '}
                        {isResendAvailable ? (
                            <Link component="button" onClick={handleResendOTP}>
                                Kirim ulang OTP
                            </Link>
                        ) : (
                            `Mohon menunggu ${String(Math.floor(countdown / 60)).padStart(2, '0')}:${String(countdown % 60).padStart(2, '0')}`
                        )}{' '}
                        atau{' '}
                        <Link href="/signin" variant="body2">
                            coba cara lain
                        </Link>
                        .
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ marginTop: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleSubmitOTP}
                            disabled={loading} // Disable the button while loading
                            sx={{
                                display: 'flex',
                                justifyContent: 'center', // Ensure the content is centered
                                alignItems: 'center', // Center the loading spinner inside the button
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Verifikasi'
                            )}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
